import React from 'react';
import styled from 'styled-components';
import {Eyebrow, Typography as Text, VerticalSpacer, RatioKeptVideo} from '..';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {AnimateOnViewWrap, UeLink} from '../../wraps';
import Grid from '@material-ui/core/Grid';
import {Button} from '../buttons';
import {CmsButtonProps} from '../buttons';
import {GridContainer} from '../grid';
import {BackgroundVideoOptionsArray} from '../../../types';
import {isValidMediaOptions} from '../../../utils';

const Styled = styled.div`
    svg {
        font-size: 7.3em;
        ${(p) => p.theme.breakpoints.down('sm')} {
            font-size: 5.7em;
        }
    }
    .paragraph {
        letter-spacing: 0.2px;
    }
    .vtc-video-wrap {
        position: relative;
    }
`;

export interface VideoAndTextCardProps {
    heading?: string;
    title?: string;
    eyebrow?: string;
    description?: string;
    accentColor?: AccentColorOptions;
    button?: CmsButtonProps;
    alignment?: 'left' | 'right';
    noGridContainer?: boolean;
    mediaOptions?: BackgroundVideoOptionsArray;
}

export const VideoAndTextCard = (props: VideoAndTextCardProps): JSX.Element | null => {
    const {
        heading,
        title,
        eyebrow,
        accentColor = 'sky',
        description,
        button = {},
        alignment = 'left',
        noGridContainer,
        mediaOptions
    } = props;

    const isSm = useBreakpointDown('sm');

    if (!isValidMediaOptions(mediaOptions)) return null;

    const videoElem = (
        <Grid item xs={12} sm={12} md={8} lg={8} xl={8} className="ue-video-and-text-card-img">
            <div className="vtc-video-wrap">
                <RatioKeptVideo controls={false} mediaOptions={mediaOptions} />
            </div>
        </Grid>
    );

    const contentElem = (
        <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
            <VerticalSpacer variant="custom" spacing={0} downSmSpacing={3} />
            {eyebrow && (
                <div className="eyebrow-container">
                    <Eyebrow text={eyebrow} as="span" />
                    <VerticalSpacer variant="custom" spacing={3} downSmSpacing={2} />
                </div>
            )}
            {(heading || title) && (
                <div className="heading-title">
                    <Text variant="h2" as="h3">
                        <UeLink href={(button && !button.label && button.href) || ''}>
                            {heading || title}
                        </UeLink>
                    </Text>
                    <VerticalSpacer variant="custom" spacing={2} downSmSpacing={1} />
                </div>
            )}
            {description && (
                <div className="description">
                    <Text variant="p">{description}</Text>
                </div>
            )}
            {button && button.label && (
                <div key="vtc-content-btn-wrap">
                    <VerticalSpacer variant="custom" spacing={3} downSmSpacing={2} />
                    <Button type={button.type} href={button.href} color={accentColor}>
                        {button.label}
                    </Button>
                </div>
            )}
        </Grid>
    );

    let aligmentElems: JSX.Element = <div className="ue-video-and-text-card-no-alignment" />;

    const right = alignment === 'right' && !isSm;
    if (right) {
        aligmentElems = (
            <>
                {videoElem}
                {contentElem}
            </>
        );
    } else {
        aligmentElems = (
            <>
                {contentElem}
                {videoElem}
            </>
        );
    }

    const el: JSX.Element = (
        <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
            className={`vtc-grid ${right ? 'right' : 'left'}`}
            spacing={isSm ? 0 : 8}>
            {aligmentElems}
        </Grid>
    );

    return (
        <AnimateOnViewWrap translateY={60}>
            <Styled className="ue-video-and-text-card">
                {noGridContainer ? el : <GridContainer>{el}</GridContainer>}
            </Styled>
        </AnimateOnViewWrap>
    );
};
